// ══════════════════════════════════════════════════════════════════════════
//  [WPV1 2026-08-02] PIPELINE / POST-PASSES — the pure passes that run over the
//  result array after classification (order matters). Extracted VERBATIM from
//  src/classifier.jsx. Dependency-free: tables + normalize + classify-det only.
// ══════════════════════════════════════════════════════════════════════════
import { ALIGN_AUTO_ASSOC_RE, ASSOC_PAIRS, INCL_ANCHORS } from "./tables.js";
import { isReferenceOnlyCorrection, normalizeText, PAYER_COMPANIES_RE, PAYER_USE_SOFT_RE, PAYER_USE_STRONG_RE, tokenize } from "./normalize.js";
import { isBlankCorrection, reorderOn } from "./classify-det.js";

// [AI-DISAGREE→HR 2026-06-12] Downgrade a Flow-1 auto Include/Exclude to Human Review when the independent
// all-lines AI verdict CONFIDENTLY disagrees. Catches rule/learned over-includes the validator missed (e.g.
// the "starter" rule firing on a diagnosis-only line). Flow-1 only + conf≥MIN (see applyAiVerdictDowngrade).
export const AI_DISAGREE_HR = true;
// [DISAGREE-60 2026-06-15] lowered 75→60. The net routes rule-vs-AI CONFLICT to Human Review (never auto-flips),
// so a lower bar = "ask a human about more conflicts," not "let the AI decide." On the MB El Dorado Hills run, 60
// caught a real false-Exclude (air-filter landmark, AI Include conf 72) AND a likely wrong-Include (aftermarket
// coil/plugs, AI Exclude conf 68) while skipping a correct tire Exclude (AI HR conf 55). +2 HR on 173 lines.
export const AI_DISAGREE_MIN_CONF = 60;

// ══════════════════════════════════════════════════════════════════════════
//  CASCADE + FLOW 2 UPGRADE
// ══════════════════════════════════════════════════════════════════════════
export function applyCascade(results) {
  const roF1Include = new Set(); const roExcludeMap = {};
  for (const r of results) { const k = r.ro || `__blank_${r.idx}`; if (r.flow === 1 && r.result === "Include") roF1Include.add(k); if (r.flow === 1 && r.result === "Exclude") { if (!roExcludeMap[k]) roExcludeMap[k] = []; roExcludeMap[k].push({ corr: r.correction || "(blank)", reason: r.reasoning }); } }
  return results.map(r => { if (r.flow !== 2 || r.result === "Exclude") return r; const k = r.ro || `__blank_${r.idx}`; if (roF1Include.has(k)) return r; const triggers = roExcludeMap[k]; if (!triggers?.length) return r; return { ...r, result: "Exclude", reasoning: `\u26D3 CASCADE \u2014 no Flow 1 includes on this RO (${triggers.map(t => `"${t.corr}"`).join(", ")} excluded)`, source: "cascade", cascadeTriggers: triggers }; });
}

export function applyFlow2Upgrade(results) {
  const roAnchorMap = {};
  for (const r of results) { if (r.result !== "Include") continue; const corrN = normalizeText(r.correction || ""); if (!INCL_ANCHORS.some(a => a.re.test(corrN))) continue; const k = r.ro || `__blank_${r.idx}`; if (!roAnchorMap[k]) roAnchorMap[k] = []; roAnchorMap[k].push(r); }
  return results.map(r => {
    if (r._ejectedFromF2) return r; // [F2-EJECT 2026-06-10] human pulled this out of Flow 2 to judge on its own merit — never re-upgrade
    const k = r.ro || `__blank_${r.idx}`; const anchors = roAnchorMap[k];
    // [F2-RELATIONAL 2026-06-10] known mechanical associates (ASSOC_PAIRS) of an anchor on THIS RO.
    const relAnchors = (anchors || []).filter(a => ASSOC_PAIRS.some(p => p.associate.test(normalizeText(r.correction || "")) && p.anchor.test(normalizeText(a.correction || ""))));
    // Exclude → Flow 2 second pass. [C6] under reorderOn() this is RELATIONAL (only rescue genuinely-associated
    // maintenance; unrelated stays Exclude on own merit). Legacy: any anchor on the RO.
    if (r.result === "Exclude" && r.reEvaluable) {
      if (r.humanDecision) return r;
      const useA = reorderOn() ? relAnchors : (anchors || []);
      if (!useA.length) return r;
      const anchorDescs = useA.map(a => { const ref = a.line ? `Line ${a.line}` : "another line"; return `"${a.correction || "included repair"}" (${ref})`; });
      return { ...r, result: "Human Review", flow: 2, anchorCorr: useA[0].correction || "", reasoning: `Included anchor on RO — ${anchorDescs[0]} — verify association`, source: r.source, _upgradedFromExclude: true, _preUpgradeReasoning: r.reasoning };
    }
    if (r.result !== "Human Review") return r; if (r.humanDecision) return r;
    // HR → Flow 2 only when a KNOWN mechanical associate of an anchor on this RO (not "any anchor"). Unrelated
    // HR stays Flow-1 HR (own merit) — never bumped to Exclude. Genuine classifyDet Flow-2 left untouched.
    if (r._upgradedToFlow2 && r.flow === 2 && !relAnchors.length) return { ...r, flow: 1, anchorCorr: null, reasoning: r._preUpgradeReasoning || "Reverted to Flow 1", _upgradedToFlow2: false };
    if (r.flow === 2 && !r._upgradedToFlow2) return r; if (!relAnchors.length) return r;
    const anchorDescs = relAnchors.map(a => { const ref = a.line ? `Line ${a.line}` : "another line"; return `"${a.correction || "included repair"}" (${ref})`; });
    const reasoning = relAnchors.length === 1 ? `Possible association with ${anchorDescs[0]} — verify` : `Possible association with ${anchorDescs.join(" or ")} — verify`;
    return { ...r, flow: 2, anchorCorr: relAnchors[0].correction || "", reasoning, source: r.source === "rules" ? "rules" : r.source, _upgradedToFlow2: true, _preUpgradeReasoning: r._preUpgradeReasoning || r.reasoning };
  });
}

// ══════════════════════════════════════════════════════════════════════════
//  [V5.1] PARTS RESCUE — upgrades reEvaluable Excludes when AI-translated
//  anchor parts exist. One-way ratchet: parts only help, never hurt.
// ══════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════
//  [V5.1.2] SAME-ANCHOR AUTO-ASSOCIATION — if this line's CCC mentions
//  an anchor and an Included line on the same RO matches that anchor,
//  auto-Include. Diagnosis line for a repair = confirmed association.
// ══════════════════════════════════════════════════════════════════════════
// [CTO-2 2026-07-03] Now REVERSIBLE and re-run on every save/undo (was run-once at classify time, so
// a stale auto-Include could outlive its excluded anchor). Flip stores _preAssocReasoning/_preAssocFlow;
// when the anchor Include disappears the line rolls back to Human Review. Derived Includes
// (_sameAnchorAssoc/_alignAutoAssoc) never anchor other lines — prevents self-sustaining chains across
// re-runs. Ejected-from-F2 lines are never re-associated (matches applyFlow2Upgrade's guard).
export function applySameAnchorAssociation(results) {
  const roIncludedAnchors = {};
  for (const r of results) {
    if (r.result !== "Include") continue;
    if (r._sameAnchorAssoc || r._alignAutoAssoc) continue; // derived Includes can't anchor others
    const corrN = normalizeText(r.correction || "");
    for (const a of INCL_ANCHORS) {
      if (a.re.test(corrN)) {
        const k = r.ro || `__blank_${r.idx}`;
        if (!roIncludedAnchors[k]) roIncludedAnchors[k] = [];
        roIncludedAnchors[k].push({ anchor: a, row: r });
      }
    }
  }
  return results.map(r => {
    if (r.humanDecision) return r;
    const k = r.ro || `__blank_${r.idx}`;
    const roAnchors = roIncludedAnchors[k] || [];
    const fullCCC = normalizeText((r.correction || "") + " " + (r.cause || "") + " " + (r.complaint || ""));
    const match = roAnchors.find(({ anchor, row }) => row !== r && anchor.re.test(fullCCC));
    // Revert: prior auto-association whose anchor Include has since disappeared rolls back to HR.
    if (r._sameAnchorAssoc && r.result === "Include") {
      if (match) return r;
      return { ...r, result: "Human Review", flow: r._preAssocFlow ?? 1, anchorCorr: null, reasoning: r._preAssocReasoning || "Anchor repair no longer included — verify on own merit", _sameAnchorAssoc: false };
    }
    if (r.result !== "Human Review") return r;
    if (r._ejectedFromF2) return r; // human pulled it out of Flow 2 — never re-associate
    if (!match) return r;
    const { anchor, row: anchorRow } = match;
    const ref = anchorRow.line ? `Line ${anchorRow.line}` : "another line";
    return { ...r, result: "Include", reasoning: `${anchor.label} — diagnosis/associated work for repair on ${ref}`, source: "rules", flow: 2, anchorCorr: anchorRow.correction || "", _sameAnchorAssoc: true, _preAssocReasoning: r._preAssocReasoning || r.reasoning, _preAssocFlow: r._preAssocFlow ?? r.flow };
  });
}

// ══════════════════════════════════════════════════════════════════════════
//  [V5.2] ALIGNMENT AUTO-ASSOCIATION — alignment op code + suspension
//  Include on same RO = auto-Include. No HR, no review needed.
// ══════════════════════════════════════════════════════════════════════════
export function applyAlignmentAutoAssoc(results) {
  // Build map of ROs with suspension/steering/driveline Includes that justify the alignment.
  // [CTO-2 2026-07-03] Uses ALIGN_AUTO_ASSOC_RE (= SUSPENSION_ANCHORS_RE minus wheel/hub bearings —
  // Blake's ruling: a bearing job doesn't imply an alignment; those stay F2 HR). Reversible + re-run
  // on saves, same pattern as applySameAnchorAssociation.
  const roSuspensionIncludes = {};
  for (const r of results) {
    if (r.result !== "Include") continue;
    if (r._sameAnchorAssoc || r._alignAutoAssoc) continue; // derived Includes can't anchor others
    const corrN = normalizeText(r.correction || "");
    if (ALIGN_AUTO_ASSOC_RE.test(corrN)) {
      const k = r.ro || `__blank_${r.idx}`;
      if (!roSuspensionIncludes[k]) roSuspensionIncludes[k] = [];
      roSuspensionIncludes[k].push(r);
    }
  }
  return results.map(r => {
    if (r.humanDecision) return r;
    const k = r.ro || `__blank_${r.idx}`;
    const suspIncludes = roSuspensionIncludes[k];
    // Revert: the suspension Include this alignment rode on has since disappeared → back to F2 HR.
    if (r._alignAutoAssoc && r.result === "Include") {
      if (suspIncludes?.length) return r;
      return { ...r, result: "Human Review", flow: 2, anchorCorr: null, reasoning: r._preAssocReasoning || "Alignment — suspension repair no longer included, verify association", _alignAutoAssoc: false };
    }
    // Only target alignment lines sent to F2 HR by the alignment routing
    if (r.result !== "Human Review") return r;
    if (!r.reasoning?.includes("Alignment")) return r;
    if (!suspIncludes?.length) return r;
    const anchor = suspIncludes[0];
    const ref = anchor.line ? `Line ${anchor.line}` : "another line";
    return { ...r, result: "Include", reasoning: `Alignment — associated with ${anchor.correction || "suspension repair"} (${ref})`, source: "rules", flow: 2, anchorCorr: anchor.correction || "", _alignAutoAssoc: true, _preAssocReasoning: r._preAssocReasoning || r.reasoning };
  });
}

export function applyPartsRescue(results, partsTranslations) {
  if (!partsTranslations || !Object.keys(partsTranslations).length) return results;
  return results.map(r => {
    if (r.result !== "Exclude" || !r.reEvaluable || r.humanDecision) return r;
    const parts = r.parts || []; const roParts = r.roParts || [];
    if (!parts.length && !roParts.length) return r;
    const lineAnchor = parts.find(p => { const t = partsTranslations[(p.partDesc || "").toUpperCase()]; return t && t.category === "include-anchor"; });
    if (lineAnchor) { const t = partsTranslations[lineAnchor.partDesc.toUpperCase()]; return { ...r, result: "Human Review", reasoning: `${r.reasoning} — but ${t.label} part on line — verify repair`, source: r.source, _partsRescue: true, partsSignal: true }; }
    const roAnchor = roParts.find(p => { const t = partsTranslations[(p.partDesc || "").toUpperCase()]; return t && t.category === "include-anchor"; });
    if (roAnchor) { const t = partsTranslations[roAnchor.partDesc.toUpperCase()]; return { ...r, result: "Human Review", reasoning: `${r.reasoning} — but ${t.label} part on Ln ${roAnchor.line || "?"} — verify association`, source: r.source, flow: 2, _partsRescue: true, partsSignal: true }; }
    return r;
  });
}

// [WPP2 2026-08-06] CORROBORATED BLANK-LINE INCLUDE — Blake-approved C1 text.
// A blank correction can be upgraded only when cause/complaint, an include-anchor part, and
// positive dollars all tell the same story. Payer guard is deliberately broader than the normal
// payer EXCLUDE rule: ANY company/strong/soft signal blocks this NEW auto-Include. On a blank line
// that mention may be the only warning, so the existing HR survives for payer logic + a human.
export function applyCorroboratedBlankInclude(results, partsTranslations = null) {
  return results.map(r => {
    if (!r || r.humanDecision || r.result !== "Human Review" || !isBlankCorrection(r.correction || "")) return r;
    // [WP38 2026-08-11] A reference marker points outside this correction for its repair story.
    // Even matching cause/complaint + parts cannot turn that incomplete evidence into an Include.
    if (isReferenceOnlyCorrection(r.correction || "")) return r;
    if (!(Number(r.partsSale) > 0) || r.cautionOutside) return r;
    const evidenceText = `${r.cause || ""} ${r.complaint || ""}`;
    if (PAYER_COMPANIES_RE.test(evidenceText) || PAYER_USE_STRONG_RE.test(evidenceText) || PAYER_USE_SOFT_RE.test(evidenceText)) return r;
    const translations = partsTranslations || r._partsTranslations || {};
    const evidenceTokens = new Set(tokenize(evidenceText));
    const matches = (r.parts || []).map(p => ({ p, t:translations[(p.partDesc || "").toUpperCase()] })).filter(({t}) => {
      if (!t || t.category !== "include-anchor") return false;
      const labelTokens = tokenize(t.label || "");
      return labelTokens.length > 0 && labelTokens.every(token => evidenceTokens.has(token));
    });
    const labels = new Set(matches.map(({t}) => normalizeText(t.label || "")));
    if (matches.length !== 1 || labels.size !== 1) return r;
    const { p, t } = matches[0]; const sale = `$${Number(r.partsSale).toFixed(2)}`;
    return { ...r, result:"Include", source:"rules", reasoning:`Parts corroborate completed repair: ${t.label} named in cause/complaint · matching ${p.partDesc || p.partNumber || t.label} on line · ${sale} parts sold.`, partsSignal:true, _corroboratedBlankInclude:true, _preCorroborationReasoning:r.reasoning };
  });
}

// [AI-DISAGREE→HR 2026-06-12] When the independent all-lines AI verdict CONFIDENTLY disagrees with a Flow-1
// auto Include/Exclude, downgrade that line to Human Review instead of auto-deciding it. This is the rules-vs-AI
// signal acting (not just observing): it catches over-includes the validator missed (e.g. a "starter" rule
// firing on a diagnosis-only line, or a "forced induction" rule firing on a reconnected vacuum line).
// SCOPE — deliberately narrow so it doesn't balloon review:
//  • Flow 1 ONLY. On Flow-2/association lines the AI verdict judges the line ALONE (can't see the anchor),
//    so it's unreliable there and would create needless HR (it flags every alignment-on-suspension line).
//  • only rule/learned/validated auto-decisions (never AI/human/cascade).
//  • only when the AI's OPPOSITE call carries confidence ≥ AI_DISAGREE_MIN_CONF.
// Reuses the existing aiDisagree surfacing (🤖 banner, AI Flags filter); keeps the original call in _ruleDecision.
export function applyAiVerdictDowngrade(results) {
  if (!AI_DISAGREE_HR) return results;
  const STAGES = new Set(["rules", "learned", "validated", "fallback"]);
  return results.map(r => {
    if (!r || r.humanDecision) return r;
    if (r.flow === 2) return r;
    if (r.result !== "Include" && r.result !== "Exclude") return r;
    if (!STAGES.has(r.source)) return r;
    const v = r._aiVerdict;
    if (!v || (v.result !== "Include" && v.result !== "Exclude")) return r;
    if (v.result === r.result) return r;                                  // agrees → keep
    if (typeof v.confidence !== "number" || v.confidence < AI_DISAGREE_MIN_CONF) return r;
    return { ...r, result: "Human Review", aiDisagree: true,
      aiDisagreeReason: v.reasoning || `Independent AI verdict says ${v.result} — verify`,
      aiConfidence: v.confidence, _ruleDecision: r.result, _ruleReasoning: r.reasoning };
  });
}

// [CTO-2 2026-07-03] The interactive re-run chain — same order as run(): cascade → Flow-2 upgrade →
// same-anchor association → alignment auto-association. The two association passes are reversible
// (flags + _preAssocReasoning), so they now re-run on every save/undo/reset — previously they ran ONCE
// at classify time and a stale auto-Include could outlive its excluded anchor. applyPartsRescue stays
// run-once in run(): it's a one-way Exclude→HR ratchet that needs partsTranslations.
export const reapplyPasses = (rows) => applyCorroboratedBlankInclude(applyAlignmentAutoAssoc(applySameAnchorAssociation(applyFlow2Upgrade(applyCascade(rows)))));
